import { toast } from "sonner";

/**
 * iter306/iter303 shared 409 handler.
 *
 * Reads a 409 response from the backend and, if it matches one of the two
 * finalization-era error shapes, surfaces a Sonner toast with a Reload
 * action. Returns `true` when the error was handled — callers should skip
 * their own toast in that case.
 *
 *   const handleConflict = useConflictHandler(reloadFn);
 *   try {
 *     await axios.patch(url, { ...body, expected_version });
 *   } catch (error) {
 *     if (handleConflict(error)) return;
 *     toast.error(error.response?.data?.detail || "Save failed");
 *   }
 *
 * Also exposes `attachExpectedVersion(payload, record)` so call sites can
 * opt into concurrency in one line without threading state.
 */
export function createConflictHandler(onReload) {
  return function handleConflict(error) {
    const detail = error?.response?.data?.detail;
    if (!detail || typeof detail !== "object") return false;

    if (detail.error === "stale_record") {
      const fields = Array.isArray(detail.field_conflicts) && detail.field_conflicts.length
        ? ` Fields changed: ${detail.field_conflicts.slice(0, 4).join(", ")}${
            detail.field_conflicts.length > 4 ? "..." : ""
          }.`
        : "";
      toast.warning(
        `Someone else saved this ${detail.entity || "record"} first.${fields} Reload to see the current values.`,
        onReload
          ? {
              action: { label: "Reload", onClick: onReload },
              duration: 10000,
            }
          : { duration: 8000 },
      );
      return true;
    }

    if (detail.error === "finalized_invoice_locked") {
      toast.warning(detail.message || "This invoice is finalized — use void or supersede to correct it.", {
        duration: 8000,
      });
      return true;
    }

    if (detail.error === "use_supersede") {
      toast.warning(
        detail.message || "Financial corrections must go through supersede so the original stays intact.",
        { duration: 8000 },
      );
      return true;
    }

    if (detail.error === "supersede_conflict") {
      toast.warning(detail.message || "This invoice has already been superseded.", {
        duration: 6000,
      });
      return true;
    }

    return false;
  };
}

export function attachExpectedVersion(payload, record) {
  if (record && typeof record.version === "number") {
    return { ...payload, expected_version: record.version };
  }
  return payload;
}
